import { useState } from 'react';

export default function PreApprovalSection() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    loanType: '',
    balanceAmount: '',
    purchasePrice: '',
    rehabBudget: '',
    afterRepairValue: '',
    annualTaxes: '',
    annualInsurance: '',
    propertyAddress: '',
    propertyType: '',
    underContract: '',
    contractFile: null as File | null,
    dealType: '',
    creditScore: '',
    liquidity: '',
    experience: '',
    timeline: '',
    additionalInfo: '',
    smsConsent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear contract file if user changes underContract to something other than "yes"
    if (name === 'underContract' && value !== 'yes') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        contractFile: null
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload a PDF or image file (JPEG, PNG, GIF, WEBP).');
        e.target.value = '';
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB.');
        e.target.value = '';
        return;
      }
    }
    setFormData(prev => ({
      ...prev,
      contractFile: file
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      // Validate phone number and SMS consent
      if (formData.phone && !formData.smsConsent) {
        setSubmitStatus('error');
        alert('Please check the SMS consent box if you are providing a phone number.');
        setIsSubmitting(false);
        return;
      }

      // Validate textarea character limit
      if (formData.additionalInfo.length > 500) {
        setSubmitStatus('error');
        alert('Additional information must be 500 characters or less.');
        setIsSubmitting(false);
        return;
      }

      // Validate contract file if under contract
      if (formData.underContract === 'yes' && !formData.contractFile) {
        setSubmitStatus('error');
        alert('Please upload the executed purchase contract.');
        setIsSubmitting(false);
        return;
      }

      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'contractFile') {
          // File uploads are displayed as "Uncollectable" per form rules
          formBody.append(key, value ? 'Uncollectable' : '');
        } else {
          formBody.append(key, value.toString());
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d5fb94b07s2llnhdbbq0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          loanType: '',
          balanceAmount: '',
          purchasePrice: '',
          rehabBudget: '',
          afterRepairValue: '',
          annualTaxes: '',
          annualInsurance: '',
          propertyAddress: '',
          propertyType: '',
          underContract: '',
          dealType: '',
          creditScore: '',
          liquidity: '',
          experience: '',
          timeline: '',
          additionalInfo: '',
          smsConsent: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="pre-approval" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Get Pre-Approved Today
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Start your financing journey with our simple, no-stress pre-approval process. No hard inquiry on your credit during pre-approval.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
              <h3 className="text-2xl font-bold text-white mb-2">Pre-Approval Application</h3>
              <div className="flex items-center text-blue-100">
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-shield-check-line"></i>
                </div>
                <span>No hard credit inquiry • Secure & confidential</span>
              </div>
            </div>

            {/* Form */}
            <form id="pre-approval-form" data-readdy-form onSubmit={handleSubmit} className="p-8">
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-5 h-5 flex items-center justify-center mr-2">
                      <i className="ri-check-circle-line text-green-600"></i>
                    </div>
                    <p className="text-green-800 font-medium">Pre-approval application submitted successfully! We'll contact you within 24-48 hours.</p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-5 h-5 flex items-center justify-center mr-2">
                      <i className="ri-error-warning-line text-red-600"></i>
                    </div>
                    <p className="text-red-800 font-medium">There was an error submitting your application. Please try again.</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Personal Information */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Enter your first name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Enter your last name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="(555) 123-4567"
                  />
                  <p className="text-xs text-gray-600 mt-2 mb-2">
                    By submitting your phone number, you authorize Fast Funding Capital LLC to send you SMS messages related to your account, appointment reminders, notification and requested services. Message frequency may vary. Message and data rates may apply. You may opt out at any time by replying STOP.
                  </p>
                  <p className="text-xs text-gray-600 mb-2">
                    I consent to receive SMS communications from Fast Funding Capital.
                  </p>
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      name="smsConsent"
                      checked={formData.smsConsent}
                      onChange={handleInputChange}
                      className="mt-1 mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-xs text-gray-700">
                      I agree to receive SMS messages from Fast Funding Capital
                    </span>
                  </label>
                </div>

                {/* Loan Information */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Loan Type *</label>
                  <select
                    name="loanType"
                    value={formData.loanType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Select loan type</option>
                    <option value="fix-flip">Fix & Flip Loans</option>
                    <option value="dscr">DSCR Loans</option>
                    <option value="commercial">Commercial Real Estate</option>
                    <option value="bridge">Bridge Loans</option>
                    <option value="rental">Rental Property Loans</option>
                    <option value="construction">Ground-Up Construction</option>
                    <option value="hard-money">Hard Money Loans</option>
                    <option value="transactional">Transactional Funding</option>
                    <option value="other">Other / Not Sure</option>
                  </select>
                </div>

                {formData.loanType === 'dscr' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Balance Amount *</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                      <input
                        type="number"
                        name="balanceAmount"
                        value={formData.balanceAmount}
                        onChange={handleInputChange}
                        required
                        min="0"
                        step="1"
                        className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="Enter exact balance amount"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Price *</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                    <input
                      type="number"
                      name="purchasePrice"
                      value={formData.purchasePrice}
                      onChange={handleInputChange}
                      required
                      min="0"
                      step="1"
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter exact purchase price"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rehab Budget *</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                    <input
                      type="number"
                      name="rehabBudget"
                      value={formData.rehabBudget}
                      onChange={handleInputChange}
                      required
                      min="0"
                      step="1"
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter exact rehab budget"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Enter 0 if no rehab needed</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">After Repair Value (ARV) *</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                    <input
                      type="number"
                      name="afterRepairValue"
                      value={formData.afterRepairValue}
                      onChange={handleInputChange}
                      required
                      min="0"
                      step="1"
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter exact estimated ARV"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Annual Taxes *</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                    <input
                      type="number"
                      name="annualTaxes"
                      value={formData.annualTaxes}
                      onChange={handleInputChange}
                      required
                      min="0"
                      step="1"
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter exact annual tax amount"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Annual Insurance *</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                    <input
                      type="number"
                      name="annualInsurance"
                      value={formData.annualInsurance}
                      onChange={handleInputChange}
                      required
                      min="0"
                      step="1"
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter exact annual insurance amount"
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Property Address *</label>
                  <input
                    type="text"
                    name="propertyAddress"
                    value={formData.propertyAddress}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Enter the property address (street, city, state, zip)"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave blank if property not yet identified</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Property Type *</label>
                  <select
                    name="propertyType"
                    value={formData.propertyType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Select property type</option>
                    <option value="single-family">Single Family Residence</option>
                    <option value="condo">Condo</option>
                    <option value="multifamily">Multifamily (2-4 units)</option>
                    <option value="apartment">Apartment Building (5+ units)</option>
                    <option value="office">Office Building</option>
                    <option value="retail">Retail Space</option>
                    <option value="industrial">Industrial/Warehouse</option>
                    <option value="mixed-use">Mixed Use</option>
                    <option value="land">Land/Development</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Is Property Under Contract? *</label>
                  <select
                    name="underContract"
                    value={formData.underContract}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Select an option</option>
                    <option value="yes">Yes, Under Contract</option>
                    <option value="no">No, Not Under Contract</option>
                    <option value="not-identified">Property Not Yet Identified</option>
                  </select>
                </div>

                {formData.underContract === 'yes' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload Executed Purchase Contract (all pages, signed) *
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        name="contractFile"
                        onChange={handleFileChange}
                        required
                        accept=".pdf,image/*"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, JPEG, PNG, GIF, WEBP (Max 10MB)</p>
                    {formData.contractFile && (
                      <div className="mt-2 flex items-center text-sm text-green-600">
                        <div className="w-4 h-4 flex items-center justify-center mr-2">
                          <i className="ri-check-line"></i>
                        </div>
                        <span>{formData.contractFile.name}</span>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Deal Type *</label>
                  <select
                    name="dealType"
                    value={formData.dealType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Select deal type</option>
                    <option value="direct-to-seller">Direct to Seller</option>
                    <option value="wholesaler-involved">Wholesaler Involved</option>
                    <option value="realtor-involved">Realtor Involved</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mid Credit Score *</label>
                  <input
                    type="number"
                    name="creditScore"
                    value={formData.creditScore}
                    onChange={handleInputChange}
                    required
                    min="300"
                    max="850"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Enter your mid credit score"
                  />
                  <p className="text-xs text-gray-500 mt-1">Typically between 300-850</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Liquidity Available for Investment *</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                    <input
                      type="number"
                      name="liquidity"
                      value={formData.liquidity}
                      onChange={handleInputChange}
                      required
                      min="0"
                      step="1"
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter exact available liquidity"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Real Estate Experience *</label>
                  <select
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Select experience level</option>
                    <option value="first-time">First Time Investor</option>
                    <option value="1-3-deals">1-3 Deals</option>
                    <option value="4-10-deals">4-10 Deals</option>
                    <option value="over-10-deals">Over 10 Deals</option>
                    <option value="professional">Real Estate Professional</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Timeline *</label>
                  <select
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">When do you need funding?</option>
                    <option value="asap">ASAP (Within 2 weeks)</option>
                    <option value="1-month">Within 1 month</option>
                    <option value="2-3-months">2-3 months</option>
                    <option value="exploring">Just exploring options</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Additional Information *</label>
                  <textarea
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                    placeholder="Tell us about your project, property details, or any specific requirements..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>
              </div>

              <div className="mt-8">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-4 px-6 rounded-lg transition-colors whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 flex items-center justify-center mr-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                      </div>
                      Processing Application...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 flex items-center justify-center mr-2">
                        <i className="ri-send-plane-line"></i>
                      </div>
                      Submit Pre-Approval Application
                    </div>
                  )}
                </button>
              </div>

              <div className="mt-4 text-center text-sm text-gray-600">
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 flex items-center justify-center mr-2">
                    <i className="ri-lock-line text-gray-400"></i>
                  </div>
                  Your information is secure and confidential. No hard credit inquiry.
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
